import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Link,
  Tab,
  Tabs,
} from '@mui/material';
import { AuthService } from '../services/AuthService';
import { User } from '../types';

interface LoginProps {
  onLogin: (user: User) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  if (value !== index) {
    return null;
  }

  return (
    <div
      role="tabpanel"
      id={`auth-tabpanel-${index}`}
      aria-labelledby={`auth-tab-${index}`}
      style={{ padding: '24px' }}
    >
      {children}
    </div>
  );
}

const Login: React.FC<LoginProps> = ({ onLogin }) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Login form state
  const [loginData, setLoginData] = useState({
    username: '',
    password: '',
  });

  // Register form state
  const [registerData, setRegisterData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const { user } = await AuthService.login(loginData.username, loginData.password);
      onLogin(user);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    if (registerData.password !== registerData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      await AuthService.register({
        username: registerData.username,
        email: registerData.email,
        password: registerData.password,
        firstName: registerData.firstName,
        lastName: registerData.lastName,
      });
      setSuccess('Registration successful! Please log in.');
      setTabValue(0);
      setRegisterData({
        username: '',
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
      });
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #000000 0%, #0a0a0a 25%, #1a1a2e 50%, #16213e 75%, #000000 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 50%, rgba(0, 212, 255, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(52, 199, 89, 0.1) 0%, transparent 50%), radial-gradient(circle at 40% 80%, rgba(255, 149, 0, 0.1) 0%, transparent 50%)',
          animation: 'pulse 4s ease-in-out infinite alternate',
        },
        '@keyframes pulse': {
          '0%': { opacity: 0.3 },
          '100%': { opacity: 0.7 },
        },
      }}
    >
      <Container component="main" maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        <div style={{ marginTop: '64px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper 
            elevation={0} 
            sx={{ 
              width: '100%', 
              marginTop: '24px',
              bgcolor: 'transparent',
              border: '2px solid rgba(0, 212, 255, 0.3)',
              borderRadius: 4,
              backdropFilter: 'blur(20px)',
              boxShadow: '0 25px 50px rgba(0, 212, 255, 0.2)',
            }}
          >
          <div style={{ borderBottom: '1px solid rgba(0, 212, 255, 0.3)' }}>
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange} 
              aria-label="auth tabs"
              sx={{
                '& .MuiTab-root': {
                  color: '#b3b3b3',
                  fontWeight: 300,
                  letterSpacing: '0.02em',
                  '&.Mui-selected': {
                    color: '#00D4FF',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#00D4FF',
                },
              }}
            >
              <Tab label="Login" />
              <Tab label="Register" />
            </Tabs>
          </div>

          <TabPanel value={tabValue} index={0}>
            <form onSubmit={handleLogin} style={{ marginTop: "8px" }}>
              <Typography 
                component="h1" 
                variant="h4" 
                align="center" 
                gutterBottom
                sx={{
                  fontWeight: 200,
                  letterSpacing: '-0.03em',
                  background: 'linear-gradient(135deg, #00D4FF, #34C759)',
                  backgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  textShadow: '0 0 30px rgba(0, 212, 255, 0.5)',
                  mb: 3,
                }}
              >
                DIGITAL TWIN 4.0
              </Typography>
              <Typography 
                variant="h6" 
                align="center" 
                gutterBottom
                sx={{
                  color: '#b3b3b3',
                  fontWeight: 300,
                  letterSpacing: '0.02em',
                  mb: 4,
                }}
              >
                Sign in to your account
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

              <TextField
                margin="normal"
                required
                fullWidth
                id="username"
                label="Username"
                name="username"
                autoComplete="username"
                autoFocus
                value={loginData.username}
                onChange={(e) => setLoginData({ ...loginData, username: e.target.value })}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="password"
                autoComplete="current-password"
                value={loginData.password}
                onChange={(e) => setLoginData({ ...loginData, password: e.target.value })}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </Button>
            </form>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <form onSubmit={handleRegister} style={{ marginTop: "8px" }}>
              <Typography component="h1" variant="h5" align="center" gutterBottom>
                Digital Twin 4.0
              </Typography>
              <Typography variant="body2" align="center" color="text.secondary" gutterBottom>
                Create a new account
              </Typography>

              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

              <TextField
                margin="normal"
                required
                fullWidth
                id="reg-username"
                label="Username"
                name="username"
                autoComplete="username"
                value={registerData.username}
                onChange={(e) => setRegisterData({ ...registerData, username: e.target.value })}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                id="reg-email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={registerData.email}
                onChange={(e) => setRegisterData({ ...registerData, email: e.target.value })}
              />
              <TextField
                margin="normal"
                fullWidth
                id="reg-firstName"
                label="First Name"
                name="firstName"
                autoComplete="given-name"
                value={registerData.firstName}
                onChange={(e) => setRegisterData({ ...registerData, firstName: e.target.value })}
              />
              <TextField
                margin="normal"
                fullWidth
                id="reg-lastName"
                label="Last Name"
                name="lastName"
                autoComplete="family-name"
                value={registerData.lastName}
                onChange={(e) => setRegisterData({ ...registerData, lastName: e.target.value })}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="password"
                label="Password"
                type="password"
                id="reg-password"
                autoComplete="new-password"
                value={registerData.password}
                onChange={(e) => setRegisterData({ ...registerData, password: e.target.value })}
              />
              <TextField
                margin="normal"
                required
                fullWidth
                name="confirmPassword"
                label="Confirm Password"
                type="password"
                id="reg-confirmPassword"
                value={registerData.confirmPassword}
                onChange={(e) => setRegisterData({ ...registerData, confirmPassword: e.target.value })}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2 }}
                disabled={loading}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>
            </form>
          </TabPanel>
        </Paper>
      </div>
    </Container>
    </Box>
  );
};

export default Login;




